/**
 * Booking Details Modal
 * 
 * Shows full details of a scheduled booking including:
 * - Project and cost centre
 * - Scheduled hours
 * - Creator information
 * - Notes
 */

import React from 'react';
import { format } from 'date-fns';
import { Calendar, User, Clock, FileText, X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  calculateEntryVariance, 
  getVarianceBadgeVariant,
  getVarianceDescription 
} from '@/services/varianceCalculationService';

interface BookingDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: {
    projectName: string;
    costCentreName: string;
    scheduledHours: {
      monday: number;
      tuesday: number;
      wednesday: number;
      thursday: number;
      friday: number;
      saturday: number;
      sunday: number;
    };
    hours?: {
      monday: number;
      tuesday: number;
      wednesday: number;
      thursday: number;
      friday: number;
      saturday: number;
      sunday: number;
    };
    isFromSchedule?: boolean;
    isDismissed?: boolean;
    dismissalReason?: string;
    dismissedAt?: Date;
    bookingCreatedByName?: string;
    bookingCreatedAt?: Date;
    bookingNotes?: string;
  };
  onDismiss?: () => void;
  showVariance?: boolean; // For approval screen
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function BookingDetailsModal({
  open,
  onOpenChange,
  booking,
  onDismiss,
  showVariance = false,
}: BookingDetailsModalProps) {
  const totalScheduledHours = DAYS.reduce(
    (sum, day) => sum + booking.scheduledHours[day],
    0
  );

  const totalActualHours = booking.hours 
    ? DAYS.reduce((sum, day) => sum + booking.hours![day], 0)
    : 0;

  const variance = showVariance && booking.isFromSchedule && booking.hours
    ? calculateEntryVariance({
        id: '',
        projectId: '',
        costCentreId: '',
        description: '',
        hours: booking.hours,
        scheduledHours: booking.scheduledHours,
        isFromSchedule: true,
      })
    : null;

  const workingDays = DAYS.filter(day => booking.scheduledHours[day] > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            Scheduled Booking Details
          </DialogTitle>
          <DialogDescription>
            Information about this scheduled booking
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Project Information */}
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Project Details
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Project:</span>
                <span className="font-medium">{booking.projectName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cost Centre:</span>
                <span className="font-medium">{booking.costCentreName}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Schedule Details */}
          <div>
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Schedule Details
            </h4>
            <div className="space-y-2 text-sm">
              {booking.bookingCreatedByName && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created by:</span>
                  <span className="font-medium">{booking.bookingCreatedByName}</span>
                </div>
              )}
              {booking.bookingCreatedAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created on:</span>
                  <span className="font-medium">
                    {format(booking.bookingCreatedAt, 'dd MMM yyyy')}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total hours:</span>
                <span className="font-medium">{totalScheduledHours}h</span>
              </div>
            </div>

            {/* Hours Breakdown */}
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Scheduled Hours:
              </div>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day, index) => {
                  const hours = booking.scheduledHours[day];
                  if (hours === 0) return null;
                  return (
                    <Badge
                      key={day}
                      variant="secondary"
                      className="text-xs"
                    >
                      {DAY_LABELS[index]}: {hours}h
                    </Badge>
                  );
                })}
              </div>
              {totalScheduledHours === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  No hours scheduled - add your actual hours
                </p>
              )}
            </div>
          </div>

          {/* Variance Analysis (for approval screen) */}
          {showVariance && variance && !booking.isDismissed && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  {variance.variance > 0 ? (
                    <TrendingUp className="h-4 w-4 text-orange-600" />
                  ) : variance.variance < 0 ? (
                    <TrendingDown className="h-4 w-4 text-blue-600" />
                  ) : (
                    <Minus className="h-4 w-4 text-green-600" />
                  )}
                  Variance Analysis
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Scheduled:</span>
                    <span className="font-medium">{variance.scheduled}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Logged:</span>
                    <span className="font-medium">{variance.actual}h</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Variance:</span>
                    <Badge variant={getVarianceBadgeVariant(variance.severity)}>
                      {variance.variance > 0 ? '+' : ''}{variance.variance}h ({variance.variance > 0 ? '+' : ''}{variance.percentage}%)
                    </Badge>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    {getVarianceDescription(variance)}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Dismissal Information (for approval screen) */}
          {showVariance && booking.isDismissed && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-orange-600">
                  <X className="h-4 w-4" />
                  Dismissal Information
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Scheduled Hours:</span>
                    <span className="font-medium">{totalScheduledHours}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Logged Hours:</span>
                    <span className="font-medium">0h</span>
                  </div>
                  {booking.dismissedAt && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Dismissed:</span>
                      <span className="font-medium">
                        {format(booking.dismissedAt, 'dd MMM yyyy HH:mm')}
                      </span>
                    </div>
                  )}
                </div>
                {booking.dismissalReason && (
                  <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <div className="text-xs font-medium text-orange-900 dark:text-orange-100 mb-1">
                      Reason:
                    </div>
                    <p className="text-xs text-orange-700 dark:text-orange-300">
                      "{booking.dismissalReason}"
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Notes */}
          {booking.bookingNotes && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-semibold mb-2">Notes:</h4>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {booking.bookingNotes}
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
          >
            Close
          </Button>
          {onDismiss && (
            <Button
              variant="destructive"
              onClick={() => {
                onDismiss();
                onOpenChange(false);
              }}
              className="w-full sm:w-auto"
            >
              Dismiss Booking
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
